import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { userApi } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useUserAuth } from "../context/UserAuthContext";

export const CheckoutDialog = ({ open, onClose }) => {
  const { items, total, clearCart, setOpen: setCartOpen } = useCart();
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", shipping_address: "",
    city: "", postal_code: "", country: "Italia",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  // Prefill from logged-in user
  useEffect(() => {
    if (user && open) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        shipping_address: f.shipping_address || user.shipping_address || "",
        city: f.city || user.city || "",
        postal_code: f.postal_code || user.postal_code || "",
        country: f.country || user.country || "Italia",
      }));
    }
  }, [user, open]);

  if (!open) return null;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error(t("cart.empty"));
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id, title: i.title, price: i.price, quantity: i.quantity,
        })),
        total,
      };
      // 1) Create the order in our DB (status=pending, payment_status=unpaid)
      const orderRes = await userApi.post("/orders", payload);
      const orderId = orderRes.data.id;

      // 2) Create Stripe Checkout session for that order and redirect
      const session = await userApi.post("/payments/checkout/session", {
        order_id: orderId,
        origin_url: window.location.origin,
      });
      if (!session.data?.url) {
        throw new Error("Sessione di pagamento non creata");
      }
      // Persist order id locally so we can show it on the success page fallback
      sessionStorage.setItem("dnj_pending_order_id", orderId);
      clearCart();
      window.location.href = session.data.url;
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (err.message || "Error"));
      setLoading(false);
    }
  };

  const handleClose = () => { setSuccess(null); onClose(); setCartOpen(false); };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div onClick={handleClose} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div data-testid="checkout-dialog"
        className="relative bg-zinc-950 border-4 border-yellow-400 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b-2 border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div>
            <div className="font-jp text-yellow-400 text-xs tracking-widest">チェックアウト</div>
            <div className="font-heading text-3xl uppercase text-white leading-none">{t("checkout.title")}</div>
          </div>
          <button data-testid="close-checkout-button" onClick={handleClose}
            className="p-2 text-white hover:bg-yellow-400 hover:text-black transition border-2 border-zinc-800">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        {success ? (
          <div className="p-10 text-center" data-testid="checkout-success">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-400 text-black mb-6 brutal-shadow-black">
              <Check className="w-10 h-10" strokeWidth={4} />
            </div>
            <h3 className="font-heading text-4xl uppercase text-white mb-3">{t("checkout.success")}</h3>
            <p className="text-zinc-400 mb-2">
              {t("checkout.success_id")} <span className="text-yellow-400 font-bold">#{success.id.slice(0, 8).toUpperCase()}</span>
            </p>
            <p className="text-zinc-400 mb-6">
              {t("checkout.success_msg")} <span className="text-yellow-400">{success.customer_email}</span>. {t("checkout.success_eta")}
            </p>
            <button data-testid="close-success-button" onClick={handleClose}
              className="bg-yellow-400 text-black font-heading uppercase px-8 py-3 hover:bg-white transition">
              {t("checkout.continue")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5">
            {[
              { k: "customer_name", l: t("checkout.name"), testid: "checkout-name" },
              { k: "customer_email", l: t("checkout.email"), type: "email", testid: "checkout-email" },
              { k: "shipping_address", l: t("checkout.address"), testid: "checkout-address" },
            ].map((f) => (
              <div key={f.k}>
                <label className="font-heading uppercase text-zinc-300 text-sm tracking-widest mb-2 block">{f.l}</label>
                <input data-testid={f.testid} type={f.type || "text"} required value={form[f.k]} onChange={onChange(f.k)}
                  className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none" />
              </div>
            ))}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="font-heading uppercase text-zinc-300 text-sm tracking-widest mb-2 block">{t("checkout.city")}</label>
                <input data-testid="checkout-city" required value={form.city} onChange={onChange("city")}
                  className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none" />
              </div>
              <div>
                <label className="font-heading uppercase text-zinc-300 text-sm tracking-widest mb-2 block">{t("checkout.postal")}</label>
                <input data-testid="checkout-postal" required value={form.postal_code} onChange={onChange("postal_code")}
                  className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="font-heading uppercase text-zinc-300 text-sm tracking-widest mb-2 block">{t("checkout.country")}</label>
              <input data-testid="checkout-country" required value={form.country} onChange={onChange("country")}
                className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none" />
            </div>

            <div className="border-t-2 border-zinc-800 pt-4 flex items-center justify-between">
              <span className="font-heading uppercase text-zinc-400 tracking-widest">{t("checkout.total")}</span>
              <span className="font-heading text-3xl text-yellow-400">€ {total.toFixed(2)}</span>
            </div>

            <button type="submit" data-testid="place-order-button" disabled={loading || items.length === 0}
              className="w-full bg-yellow-400 text-black font-heading uppercase text-xl py-4 hover:bg-white transition disabled:opacity-40">
              {loading ? t("checkout.submitting") : t("checkout.submit")}
            </button>
            <p className="text-center text-zinc-500 text-xs uppercase tracking-widest">{t("checkout.disclaimer")}</p>
          </form>
        )}
      </div>
    </div>
  );
};
